/**
 * MasterfileEditor — a single-textarea admin surface for the "operator's
 * manual" that governs how the AI must use the Knowledge Library.
 * Persisted to `ai_knowledge_masterfile` and prepended to every AI system
 * prompt on the platform (chat + Prof. FreedrIQ + LMS AI Builders).
 */
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { BookLock, Save, RefreshCw } from 'lucide-react';

export default function MasterfileEditor() {
  const { API } = useContext(AuthContext);
  const [content, setContent] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/ai-masterfile`);
      setContent(data.content || '');
      setEnabled(Boolean(data.enabled));
      setMeta({
        updated_at: data.updated_at,
        updated_by_email: data.updated_by_email,
      });
    } catch {
      toast.error('Failed to load Masterfile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/ai-masterfile`, { content, enabled });
      setMeta({
        updated_at: data.updated_at,
        updated_by_email: data.updated_by_email,
      });
      toast.success('Masterfile saved — the AI will use it on the next question');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="masterfile-editor">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BookLock className="w-5 h-5 text-indigo-500" />
              Knowledge Library Masterfile
            </CardTitle>
            <CardDescription>
              Permanent operator&apos;s manual for how the AI must use the knowledge library.
              This text is prepended to every AI system prompt platform-wide (chat,
              Prof. FreedrIQ, lesson builders) and always takes precedence over other
              instructions. Use it for style rules, terminology conventions, safety
              notices, or &ldquo;always cite Section 4&rdquo; style policies.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="masterfile-enabled-toggle">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-4 w-4"
              />
              <span>{enabled ? 'Active' : 'Paused'}</span>
            </label>
            <Button variant="outline" size="sm" onClick={load} disabled={loading || saving} data-testid="masterfile-reload">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Reload
            </Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="masterfile-save">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving…' : 'Save Masterfile'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={`# House style
- Always metric-first; imperial in parentheses.
- Never guess machine specs — cite the operating manual instead.

# Terminology
- Use "primary drying" not "first dry".

# Safety
- Any answer about vacuum pumps must remind the user to check oil level before startup.`}
          rows={16}
          className="font-mono text-sm"
          data-testid="masterfile-textarea"
        />
        <div className="flex items-center justify-between text-[11px] text-slate-500">
          <span data-testid="masterfile-char-count">{content.length.toLocaleString()} / 32,000 characters</span>
          {meta?.updated_at ? (
            <span>
              Last saved {new Date(meta.updated_at).toLocaleString()}
              {meta.updated_by_email ? ` by ${meta.updated_by_email}` : ''}
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
